/**
 * War das eine echte Geste — oder ein nachgereichtes Ereignis? (19.09.2026)
 *
 * Martin: *„Immer wenn ich hier auf Aufnahme gedrueckt habe und absenden und
 * dann zu Vespera wechsle, dann passiert das, dass dann der Push-to-Talk
 * auch automatisch geklickt ist. Das ist total bloed."*
 *
 * Am Protokoll belegt: `via=knopf` wird ausschliesslich in
 * `onPttPointerDown` gesetzt — es war ein ECHTES Zeigerereignis auf dem
 * Knopf, kein programmatischer Start. Das Ereignis entstand nur nicht jetzt.
 *
 * ⚠️ Eine Zeitsperre („ignoriere 400 ms nach der Rueckkehr") waere geraten —
 *    niemand weiss, wie schnell Martin wirklich tippt, und eine zu grosse
 *    Frist macht den Knopf traege. Der Zeitstempel sagt es genau:
 *    `ev.timeStamp` und `performance.now()` teilen denselben Nullpunkt. Ein
 *    Ereignis, das VOR dem Sichtbarwerden entstanden ist, kann keine Geste
 *    von jetzt sein.
 *
 * Eine echte Geste nach der Rueckkehr traegt immer eine spaetere Zeit und
 * geht ungehindert durch. Die Regel kostet also nichts.
 */

/**
 * @param zeitstempel `ev.timeStamp` des Zeigerereignisses
 * @param sichtbarSeit `performance.now()` beim letzten Wechsel auf sichtbar,
 *                     oder null/0, wenn es noch keinen gab
 */
export function istNachgereichterDruck(
  zeitstempel: number | undefined,
  sichtbarSeit: number | null,
): boolean {
  // ⚠️ Lieber einmal zu viel zulassen als den Knopf tot machen: ohne
  //    verlaessliche Zahlen wird nichts geblockt.
  if (!zeitstempel || !sichtbarSeit) return false;
  // Gleichstand ist kein Beweis — im Zweifel gewinnt der Mensch.
  return zeitstempel < sichtbarSeit;
}

/**
 * Wie lange lag der Finger auf dem Knopf, WÄHREND wirklich aufgenommen
 * wurde? (23.09.2026, Ticket 2494)
 *
 * Martin: *„Beim Klick auf Push-to-Talk geht die Aufnahme kurz an und sofort
 * wieder weg."* Nach pre.3202 kam dasselbe Bild noch fünfmal, jedes Mal mit
 * `gesperrt=nein`: 15:32:29, 15:48:45 und 16:02:32 hielt er 1724–2110 ms, das
 * kalte Mikro war erst nach 1685–2060 ms offen, aufgenommen wurden 39–51 ms.
 * 18:05:25 gehalten 552 ms, offen nach 505 ms. 23:22:02 gehalten 679 ms,
 * offen nach 503 ms. Danach jedes Mal `verworfen (zu leise)` und Sekunden
 * später ein neuer Druck.
 *
 * ⚠️ Die Grenze zwischen Tipp und Halten (PTT_TIPP_MS in app.ts) lief ab dem
 *    DRUCK. Wer während des Kaltstarts hält, hat beim Loslassen längst
 *    „gehalten", obwohl das Mikro erst Millisekunden offen ist, und das
 *    Loslassen schickt Stille ab. Gezählt wird jetzt ab dem späteren der
 *    beiden Zeitpunkte, Druck oder Aufnahmebeginn.
 *
 * ⚠️ Ob der Finger wirklich so lange lag, ist NICHT belegt. In vier der
 *    fünf Fälle kam das Loslassen 37–48 ms nach Aufnahmebeginn, schneller
 *    als jede Reaktion. Ebenso gut kann das pointerup während des
 *    Kaltstarts verspätet zugestellt worden sein (gemessen wird im Handler,
 *    nicht ab ev.timeStamp). Die Regel trifft beide Deutungen; welche
 *    stimmt, zeigt `verzug=` in der Zeile `[ptt-einrasten]` (app.ts).
 *
 * ⚠️ `gehaltenMs` selbst bleibt die Uhr am Finger. Die Meldung „Zu kurz" in
 *    pttFinishAndSend braucht genau die (Kommentar vom 31.08. dort).
 *
 * @param gehaltenMs Loslassen minus Druck (`pttReleaseHeld`)
 * @param aufnahmeMs wie lange der Rekorder beim Loslassen schon lief
 */
export function halteZeitAbAufnahme(gehaltenMs: number, aufnahmeMs: number): number {
  // Ohne verlässliche Zahl vom Rekorder gilt der Finger, wie bisher.
  if (!Number.isFinite(aufnahmeMs) || aufnahmeMs < 0) return gehaltenMs;
  return Math.min(gehaltenMs, aufnahmeMs);
}
